import { useState } from 'react';
import { Button } from 'src/components/Button';
import { Heading } from 'src/components/Heading';
import { DropZone } from '../DropZone';
import { FieldMappingDndProvider } from './FieldMappingDndProvider';
import { SourceColumn } from './SourceColumn';
import { useCrmSync } from '../../contexts/CrmSyncContext';
import { useFieldMapping } from '../../contexts/FieldMappingContext';
import { useContacts } from '../../contexts/ContactsContext';
import { useNotification } from 'src/contexts/NotificationContext';

export const FieldMapping = () => {
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const notification = useNotification();

  const { integratedCrm, segmentId, syncInProgress, handleStepNavigation } =
    useCrmSync();

  const {
    isFieldMappingValid,
    availableCrmFields,
    mappingEntries,
    isInitialized,
    searchQuery,
    setSearchQuery,
    sampleContacts,
    filteredCrmFields,
    requiredFields,
    optionalFields,
    removeFieldFromMapping,
  } = useFieldMapping();

  const { handlePreviewUpload } = useContacts();

  const handleBack = () => handleStepNavigation('back');
  const handleContinue = async () => {
    try {
      setIsLoadingPreview(true);
      await handlePreviewUpload(
        integratedCrm,
        segmentId,
        handleStepNavigation,
        notification,
      );
    } catch (error) {
      // Error handling is already done in handlePreviewUpload
      console.error('Preview upload failed:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };
  const disabled = syncInProgress;

  const handleFieldRemoved = (campaignField: string, crmFieldValue: string) => {
    removeFieldFromMapping(campaignField, crmFieldValue);
  };

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-neutral-500">Loading field mapping...</div>
      </div>
    );
  }

  return (
    <>
      <Heading
        title="Map schema"
        subtitle="Map columns from your CRM to Fluents properties"
        backAction={handleBack}
      >
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outlined"
            onClick={handleBack}
            disabled={disabled}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleContinue}
            disabled={disabled || !isFieldMappingValid || isLoadingPreview}
          >
            {isLoadingPreview ? 'Loading Preview...' : 'Preview Upload'}
          </Button>
        </div>
      </Heading>

      <FieldMappingDndProvider>
        <div className="max-w-6xl w-full flex-1 min-h-0">
          <div className="grid grid-cols-[350px_1fr] gap-8 h-full">
            {/* Left Column - CRM Fields */}
            <SourceColumn
              filteredCrmFields={filteredCrmFields}
              sampleContacts={sampleContacts}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />

            {/* Right Column - Fluents Properties */}
            <div className="space-y-8 py-6 pr-4 overflow-y-auto">
              {/* Required Properties */}
              <section>
                <h3 className="text-base font-semibold text-neutral-700 mb-6">
                  Required properties
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  {requiredFields.map((field) => {
                    const mapping = mappingEntries.find(
                      (m) => m.campaignField === field.name,
                    );
                    const mappedFields =
                      mapping?.crmFieldValues
                        .map((crmFieldValue) =>
                          availableCrmFields.find(
                            (crmField) => crmField.value === crmFieldValue,
                          ),
                        )
                        .filter((crmField) => crmField != null) ?? [];

                    return (
                      <DropZone
                        key={field.name}
                        campaignField={field.name}
                        fieldLabel={field.label}
                        mappedFields={mappedFields}
                        sampleContacts={sampleContacts}
                        required={field.required}
                        disabled={disabled}
                        onFieldRemoved={handleFieldRemoved}
                      />
                    );
                  })}
                </div>
              </section>

              {/* Optional Properties */}
              {optionalFields.length > 0 && (
                <section>
                  <h3 className="text-base font-semibold text-neutral-700 mb-6">
                    Optional properties
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    {optionalFields.map((field) => {
                      const mapping = mappingEntries.find(
                        (m) => m.campaignField === field.name,
                      );
                      const mappedFields =
                        mapping?.crmFieldValues
                          .map((crmFieldValue) =>
                            availableCrmFields.find(
                              (crmField) => crmField.value === crmFieldValue,
                            ),
                          )
                          .filter((crmField) => crmField != null) ?? [];

                      return (
                        <DropZone
                          key={field.name}
                          campaignField={field.name}
                          fieldLabel={field.label}
                          mappedFields={mappedFields}
                          sampleContacts={sampleContacts}
                          required={field.required}
                          disabled={disabled}
                          onFieldRemoved={handleFieldRemoved}
                        />
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </FieldMappingDndProvider>
    </>
  );
};
